import React from 'react';
import { Typography, IconButton, Grid, Card, CardContent, List, ListItem, Box, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  EditOutlined as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Circle,
  CircleOutlined,
  Code,
  LocationOn,
  Email,
  WorkHistory,
  Business,
  Work,
  Language,
} from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';

export const RenderProfileDetail = ({ icon, label, value }) => (
  <Typography py={0.1}>
    <IconButton color='primary'>{icon}</IconButton>
    {label} {value || 'N/A'}
  </Typography>
);

export const ProfileCard = ({ user, onEdit, isCurrentUser }) => {
  const renderProfileDetails = details => {
    return details.map((detail, index) => (
      <RenderProfileDetail
        key={index}
        icon={detail.icon}
        label={detail.label}
        value={detail.value}
        onEdit={() => onEdit(detail.label, { [detail.field]: detail.value })}
        isCurrentUser={isCurrentUser}
      />
    ));
  };

  return (
    <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
      <Box display='flex' alignItems='center' flexDirection={'row'}>
        <List>
          {renderProfileDetails([
            { value: user?.summary, field: 'summary' },
            { icon: <Code />, label: 'Tech Stack:', value: user?.techStack, field: 'techStack' },
            {
              icon: <LocationOn />,
              label: 'Office Location:',
              value: user?.officeLocation,
              field: 'officeLocation',
            },
            { icon: <Email />, label: 'Email:', value: user?.email, field: 'email' },
            { icon: <Work />, label: 'Designation:', value: user?.designation, field: 'designation' },
            { icon: <Business />, label: 'Department:', value: user?.department, field: 'department' },
            {
              icon: <WorkHistory />,
              label: 'Experience:',
              value: `${Math.floor(user?.experience)} Years`,
              field: 'experience',
            },
            {
              icon: <Language />,
              label: 'Website:',
              value: (
                <a
                  href={user?.personalWebsite}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ textDecoration: 'none' }}
                >
                  {user?.personalWebsite ? user?.personalWebsite : 'N/A'}
                </a>
              ),
            },
          ])}
        </List>
      </Box>
    </Box>
  );
};

export const RenderCard = ({
  title,
  items,
  showAll,
  showAllKey,
  sliceCount,
  renderItem,
  handleShowAllToggle,
  onEdit,
  isCurrentUser,
}) => (
  <Grid item xs={12}>
    <Card sx={{ borderRadius: '10px', boxShadow: 'none', border: 1, borderColor: 'divider', flex: 1 }}>
      <CardContent>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' fontWeight='bold'>
            {title}
          </Typography>
          {isCurrentUser && onEdit && (
            <IconButton onClick={onEdit}>
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <List>
          {(showAll[showAllKey] ? items : items?.slice(0, sliceCount))?.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              {renderItem(item)}
            </ListItem>
          ))}
        </List>
      </CardContent>
      {items?.length > sliceCount && (
        <Box sx={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid lightgrey' }}>
          <Button
            fullWidth
            onClick={() => handleShowAllToggle(showAllKey)}
            endIcon={<ArrowForwardIcon />}
            sx={{ fontWeight: 'bold' }}
          >
            {showAll[showAllKey] ? `Hide All ${title}` : `Show All ${items?.length} ${title}`}
          </Button>
        </Box>
      )}
    </Card>
  </Grid>
);

export const SkillRenderCard = ({ title, items, showAll, showAllKey, sliceCount, renderItem, handleShowAllToggle }) => {
  return (
    <Card sx={{ borderRadius: '10px', boxShadow: 'none', border: 1, borderColor: 'divider', flex: 1 }}>
      <CardContent>
        <Typography variant='h5' fontWeight={'bold'} gutterBottom>
          {title}
        </Typography>
        {items?.map(category => (
          <Accordion key={category._id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='body1'>{category.category}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {category.skills.map(skill => (
                <ListItem
                  key={skill._id}
                  sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
                >
                  <Typography>{skill.name}</Typography>
                  <ProficiencyDots proficiency={skill.proficiency} />
                </ListItem>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );
};

export const ProficiencyDots = ({ proficiency }) => {
  const filledDots = proficiency === 'Advanced' ? 5 : proficiency === 'Intermediate' ? 3 : 2;
  const totalDots = 5;

  return (
    <Box>
      {Array.from({ length: filledDots }).map((_, index) => (
        <Circle fontSize='12px' key={`filled-${index}`} color='primary' />
      ))}
      {Array.from({ length: totalDots - filledDots }).map((_, index) => (
        <CircleOutlined fontSize='12px' key={`unfilled-${index}`} color='primary' />
      ))}
    </Box>
  );
};
